<template>
  <div>
    <div class="profile__charts__container">
      <div v-show="selectedChart === null" class="sales_report__container">
        <ReportFilter
          :load-reports="loadReports"
          :has-metrics="true"
          report-type="detail"
          report-filename="SalesReport"
        />
      </div>

      <hr
        v-if="selectedChart === null && canViewRequestReport"
        style="width: 100%; height: 0.1rem"
      />

      <div v-if="salesReportParams" class="w-100">
        <h2 class="mb-4">Metrics</h2>
        <div
          class="is-flex"
          style="flex-wrap: wrap; gap: 1.25rem; justify-content: center"
        >
          <ReportItem
            :loading="areReportsloading"
            :style="`display: ${
              selectedChart === null || selectedChart === 'requestsByStage'
                ? 'inherit'
                : 'none'
            };`"
            :chart-data="requestsByStage"
            :chart-options="requestsByStageOptions"
            :chart-type="'pie'"
            :selected-chart="selectedChart"
            :report-name="'requestsByStage'"
            :report-title="'Requests by Stage'"
            :excel-report-title="'RequestsByStageReport'"
            :open-selected-source-data-table="openSelectedSourceDataTable"
            :export-table-to-excel="exportTableToExcel"
            :return-to-reports-view="returnToReportsView"
            :set-selected-chart="setSelectedChart"
            :charts-types="['pie', 'funnel', 'line']"
            :height="'500px'"
            :min-height="'200px'"
          >
            <div
              slot="tags__section"
              class="is-flex"
              style="gap: 0.5rem; flex-wrap: wrap"
            >
              <b-tag>
                {{ salesReportParams.clientsText }}
              </b-tag>

              <b-tag> From {{ salesReportParams.from }} </b-tag>

              <b-tag> To {{ salesReportParams.to }} </b-tag>
            </div>
          </ReportItem>

          <ReportItem
            :style="`display: ${
              selectedChart === null || selectedChart === 'time'
                ? 'inherit'
                : 'none'
            };`"
            :chart-data="requestsByTime"
            :chart-options="requestsByTimeOptions"
            :chart-type="'line'"
            :selected-chart="selectedChart"
            :report-name="'time'"
            :report-title="'Requests by Time'"
            :excel-report-title="'QuotesByTimeReport'"
            :open-selected-source-data-table="openSelectedSourceDataTable"
            :export-table-to-excel="exportTableToExcel"
            :return-to-reports-view="returnToReportsView"
            :set-selected-chart="setSelectedChart"
            :height="'400px'"
            :min-height="'200px'"
            :loading="areReportsloading"
          >
            <div
              slot="tags__section"
              class="is-flex"
              style="gap: 0.5rem; flex-wrap: wrap"
            >
              <b-tag>
                {{ salesReportParams.clientsText }}
              </b-tag>
            </div>
          </ReportItem>

          <ReportItem
            :style="`display: ${
              selectedChart === null || selectedChart === 'clients'
                ? 'inherit'
                : 'none'
            };`"
            :chart-data="requestsByClients"
            :chart-options="requestsByClientOptions"
            :chart-type="'doughnut'"
            :selected-chart="selectedChart"
            :report-name="'clients'"
            :report-title="'Requests by Clients'"
            :excel-report-title="'QuotesByClientsReport'"
            :open-selected-source-data-table="openSelectedSourceDataTable"
            :export-table-to-excel="exportTableToExcel"
            :return-to-reports-view="returnToReportsView"
            :set-selected-chart="setSelectedChart"
            :loading="areReportsloading"
            :height="'400px'"
            :min-height="'200px'"
          >
            <div
              slot="tags__section"
              class="is-flex"
              style="gap: 0.5rem; flex-wrap: wrap"
            >
              <b-tag>
                {{ salesReportParams.clientsText }}
              </b-tag>

              <b-tag> From {{ salesReportParams.from }} </b-tag>

              <b-tag> To {{ salesReportParams.to }} </b-tag>
            </div>
          </ReportItem>

          <ReportItem
            :style="`display: ${
              selectedChart === null || selectedChart === 'destinations'
                ? 'inherit'
                : 'none'
            };`"
            :chart-data="requestsByDestinations"
            :chart-options="requestsByDestinationOptions"
            :chart-type="'bar'"
            :selected-chart="selectedChart"
            :report-name="'destinations'"
            :report-title="'Requests by Destinations'"
            :excel-report-title="'QuotesByDestinationReport'"
            :open-selected-source-data-table="openSelectedSourceDataTable"
            :export-table-to-excel="exportTableToExcel"
            :return-to-reports-view="returnToReportsView"
            :set-selected-chart="setSelectedChart"
            :loading="areReportsloading"
            :legend="'(*) <strong>Multicountry Requests Included:</strong> The data includes multicountry requests where multiple destinations are combined'"
            :height="'400px'"
            :min-height="'200px'"
          >
            <div
              slot="tags__section"
              class="is-flex"
              style="gap: 0.5rem; flex-wrap: wrap"
            >
              <b-tag>
                {{ salesReportParams.clientsText }}
              </b-tag>

              <b-tag> From {{ salesReportParams.from }} </b-tag>

              <b-tag> To {{ salesReportParams.to }} </b-tag>
            </div>
          </ReportItem>
        </div>
      </div>
    </div>
    <b-modal
      v-model="isSourceDataModalOpen"
      title="Source Data"
      size="xl"
      @close="closeModal()"
    >
      <div class="source_data_modal__container">
        <h1 class="mb-2">Source data</h1>
        <b-table
          v-if="selectedSourceData && selectedSourceDataColumns"
          class="source_data_table"
          :data="selectedSourceData"
          :columns="selectedSourceDataColumns"
          hoverable
        >
        </b-table>
      </div>
    </b-modal>
    <div class="excel_table__container" style="display: none">
      <b-table
        v-if="selectedSourceData && selectedSourceDataColumns"
        class="source_data_table"
        :data="selectedSourceData"
        :columns="selectedSourceDataColumns"
        hoverable
      >
      </b-table>
    </div>
  </div>
</template>

<script>
import ReportItem from "@/components/base-ui/ReportItem.vue";
import { mapGetters } from "vuex";
import { DEFAULT_ALLOWED } from "@/constants";
import * as XLSX from "xlsx";
import ReportFilter from "@/components/reports/ReportFilter.vue";

export default {
  name: "RequestReport",
  components: { ReportFilter, ReportItem },

  data() {
    return {
      isLoadingUsers: false,
      salesReportParams: null,
      requestsByTimeOptions: {
        responsive: true,
        plugins: {
          legend: {
            display: true,
          },
          title: {
            display: false,
            text: "Monthly Sales",
          },
        },
        scales: {
          y: {
            beginAtZero: true,
          },
        },
      },
      requestsByClientOptions: {
        responsive: true,
        plugins: {
          legend: {
            position: "bottom",
          },
          title: {
            display: false,
            text: "Doughnut Chart Example",
          },
        },
      },
      requestsByDestinationOptions: {
        responsive: true,
        scales: {
          x: {
            beginAtZero: true, // Ensures bars start from zero
          },
        },
        plugins: {
          legend: {
            position: "bottom", // Positions the legend at the top
          },
          title: {
            display: false,
            text: "Requests by Destinations", // Title of the chart
          },
        },
      },
      requestsByStageOptions: {
        responsive: true,
        scales: {
          x: {
            beginAtZero: true, // Ensures bars start from zero
          },
        },
        plugins: {
          legend: {
            position: "bottom", // Positions the legend at the top
          },
          title: {
            display: false,
            text: "Requests by Stage", // Title of the chart
          },
        },
        loading: false,
      },
      loading: true,
      selectedChart: null,
      selectedSourceData: null,
      selectedSourceDataColumns: null,
      isSourceDataModalOpen: false,
      areReportsloading: false,
    };
  },
  computed: {
    ...mapGetters({
      authData: "auth/getAuthData",
      requestsByTime: "reports/getQuotesByTime",
      requestsByClients: "reports/getQuotesByClients",
      requestsByStage: "reports/getRequestsByStages",
      requestsByDestinations: "reports/getRequestsByDestinations",
      salesReport: "reports/getSalesReport",
      userSummary: "reports/getUserSummary",
      clients: "client/getClients",
      authUser: "auth/getAuthUserData",
      users: "users/getUsers",
    }),
    canViewRequestReport() {
      if (process.env.NODE_ENV !== "production") return true;

      const allowed = DEFAULT_ALLOWED.concat([
        "jloaiza@encounterlatinamerica.com",
        "jloaiza@ela.travel",
      ]);

      return allowed.includes(this.authUser.email);
    },
  },
  methods: {
    setSelectedSourceData(sourceData) {
      const { data, columns } = this.transformToTableData(sourceData);

      this.selectedSourceData = data;
      this.selectedSourceDataColumns = columns;
    },
    setSelectedChart(chart) {
      this.selectedChart = chart;
    },
    async loadQuotesByClients(params) {
      const adaptedParams = {
        client: params?.clients?.map((client) => client.id),
        from: params?.from,
        to: params?.to,
        user: params?.users?.map((user) => user.id),
      };

      await this.$store.dispatch("reports/getQuotesByClients", adaptedParams);
    },
    async loadQuotesByTime(params) {
      const adaptedParams = {
        client: params?.clients?.map((client) => client.id),
        from: params?.from,
        to: params?.to,
        user: params?.users?.map((user) => user.id),
      };

      await this.$store.dispatch("reports/getQuotesByTime", adaptedParams);
    },
    async loadQuotesByDestinations(params) {
      const adaptedParams = {
        client: params?.clients?.map((client) => client.id),
        from: params?.from,
        to: params?.to,
        user: params?.users?.map((user) => user.id),
      };

      await this.$store.dispatch(
        "reports/loadQuotesByDestinations",
        adaptedParams
      );
    },
    async getRequestsByStages(params = {}) {
      const adaptedParams = {
        client: params?.clients?.map((client) => client.id),
        from: params?.from,
        to: params?.to,
        user: params?.users?.map((user) => user.id),
      };

      await this.$store.dispatch("reports/getRequestsByStages", adaptedParams);
    },
    async loadReports(salesReportParams) {
      this.salesReportParams = salesReportParams;

      this.areReportsloading = true;

      await Promise.allSettled([
        await this.loadQuotesByTime(salesReportParams),
        await this.loadQuotesByClients(salesReportParams),
        await this.loadQuotesByDestinations(salesReportParams),
        await this.getRequestsByStages(salesReportParams),
      ]);

      this.areReportsloading = false;
    },
    closeModal() {
      this.isSourceDataModalOpen = false;
    },
    transformToTableData(chartData) {
      const data = chartData.labels.map((label, index) => ({
        country: label,
        quotes: chartData.datasets[0].data[index],
      }));

      const columns = [
        { field: "country", label: "Country" },
        { field: "quotes", label: "Requests by Country", numeric: true },
      ];

      return { data, columns };
    },
    openSelectedSourceDataTable(sourceData = null) {
      const { data, columns } = this.transformToTableData(sourceData);

      this.selectedSourceData = data;
      this.selectedSourceDataColumns = columns;
      this.isSourceDataModalOpen = sourceData !== null;
    },
    returnToReportsView($event) {
      $event.stopPropagation();
      this.setSelectedChart(null);
    },
    exportTableToExcel(sourceData, filename = "table") {
      this.setSelectedSourceData(sourceData);
      this.$nextTick(() => {
        const table = document.querySelector(".excel_table__container table");
        if (!table) {
          console.error("Table element not found!");
          return;
        }

        const workbook = XLSX.utils.table_to_book(table, { sheet: "Sheet1" });
        XLSX.writeFile(workbook, `${filename}.xlsx`);
      });
    },
  },
};
</script>
